import React, { useEffect, useMemo, useState } from 'react';
import Loader from './components/Loader';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useScrollAnimation from './hooks/useScrollAnimation';
import './blog.css';
import './animations.css';

// Format date helper
function formatDate(date) {
  if (!date) return '';
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  if (typeof date === 'string') return date;
  return date.toString();
}

// Dummy blog details data for immediate visual testing
const dummyBlogDetails = {
  'minimalist-design': {
    id: 'minimalist-design',
    title: "The Art of Minimalist Interior Design",
    summary: "Explore how less can truly be more when it comes to creating serene, functional spaces that breathe with intention and purpose.",
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80",
    category: "Design Philosophy",
    author: "Sarah Mitchell",
    date: "2025-01-15",
    readTime: "5 min read",
    tags: ["minimalism", "interior design", "home decor", "sustainability"],
    content: `
      <p>In an era where our homes are filled with endless possessions, minimalist interior design offers a refreshing alternative that focuses on what truly matters. This design philosophy isn't just about having fewer things—it's about creating spaces that serve your life, rather than demanding constant attention.</p>
      
      <h2>The Principles of Minimalist Design</h2>
      
      <p>At its core, minimalist design is guided by the principle that every element in a space should have a purpose. This doesn't mean your home should feel cold or sterile. Instead, it means choosing pieces that you love and that serve a function, while eliminating the excess that creates visual and mental clutter.</p>
      
      <blockquote>
        "The ability to simplify means to eliminate the unnecessary so that the necessary may speak." — Hans Hofmann
      </blockquote>
      
      <h2>Creating Calm Through Color</h2>
      
      <p>One of the most powerful tools in minimalist design is a carefully curated color palette. Neutral tones—whites, creams, soft grays, and warm beiges—create a sense of calm and allow natural light to bounce throughout the space. These colors also provide a timeless backdrop that won't feel dated in years to come.</p>
      
      <p>However, minimalism doesn't mean your space has to be devoid of personality. A single piece of art, a carefully chosen throw pillow, or a beautiful vase can add character without overwhelming the space.</p>
      
      <h2>Quality Over Quantity</h2>
      
      <p>In minimalist design, every piece counts. This means investing in well-made furniture and accessories that will stand the test of time, both in terms of durability and style. A beautifully crafted wooden dining table, for example, can serve as both a functional piece and a work of art.</p>
      
      <p>This approach often proves more economical in the long run, as quality pieces don't need frequent replacement and maintain their aesthetic appeal over time.</p>
      
      <h2>The Impact on Daily Life</h2>
      
      <p>Living in a minimalist space has profound effects on daily life. With fewer objects to clean, organize, and maintain, you'll find more time for the activities and relationships that truly matter. The visual calm of a well-designed minimalist space can also reduce stress and improve focus.</p>
      
      <p>Many of our clients report feeling more at peace in their homes after embracing minimalist principles, describing their spaces as true sanctuaries from the busy world outside.</p>
    `
  },
  'sustainable-materials': {
    id: 'sustainable-materials',
    title: "Sustainable Materials in Modern Architecture",
    summary: "How we're incorporating eco-friendly materials and practices into contemporary design without compromising on aesthetics or functionality.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
    category: "Sustainability",
    author: "David Chen",
    date: "2025-01-10",
    readTime: "7 min read",
    tags: ["sustainability", "eco-friendly", "materials", "architecture"],
    content: `
      <p>The construction industry is undergoing a green revolution, and sustainable materials are at the forefront of this transformation. As designers and architects, we have a responsibility to create spaces that not only serve their occupants but also respect our planet's resources.</p>
      
      <h2>The Rise of Reclaimed Wood</h2>
      
      <p>One of our favorite sustainable materials is reclaimed wood. Each piece tells a story—whether it's barn wood from a 19th-century farmhouse or factory flooring from an old manufacturing plant. Beyond its environmental benefits, reclaimed wood adds character and warmth that's impossible to replicate with new materials.</p>
      
      <p>In our recent HARBOR VIEW residence project, we used reclaimed oak beams to create stunning ceiling features that serve as the focal point of the main living area. The natural patina and unique grain patterns create visual interest while reducing our environmental footprint.</p>
      
      <h2>Innovative Eco-Materials</h2>
      
      <p>The world of sustainable materials extends far beyond reclaimed wood. We're now incorporating materials like:</p>
      
      <ul>
        <li><strong>Bamboo:</strong> Fast-growing and incredibly durable, perfect for flooring and cabinetry</li>
        <li><strong>Cork:</strong> Renewable and naturally antimicrobial, ideal for wall coverings and flooring</li>
        <li><strong>Recycled Steel:</strong> Strong, durable, and infinitely recyclable</li>
        <li><strong>Low-VOC Paints:</strong> Better for indoor air quality and the environment</li>
      </ul>
      
      <h2>The Beauty of Natural Stone</h2>
      
      <p>When sourced responsibly, natural stone offers unmatched durability and timeless beauty. Local stone not only reduces transportation emissions but also connects buildings to their regional landscape. In our designs, we often feature locally quarried limestone or granite as both structural and decorative elements.</p>
      
      <blockquote>
        "Sustainable design isn't a limitation—it's an opportunity to create more thoughtful, meaningful spaces."
      </blockquote>
      
      <h2>Performance Benefits</h2>
      
      <p>Many sustainable materials actually outperform their conventional counterparts. Bamboo flooring, for instance, is harder than most traditional hardwoods. Hemp-based insulation provides excellent thermal performance while being completely natural and non-toxic.</p>
      
      <p>These materials often contribute to better indoor air quality, improved energy efficiency, and greater occupant comfort—proving that environmental responsibility and human wellness go hand in hand.</p>
      
      <h2>Looking Forward</h2>
      
      <p>As technology advances, we're seeing exciting developments in bio-based materials, including mycelium-based insulation and algae-derived paints. The future of sustainable design is bright, and we're committed to staying at the forefront of these innovations.</p>
    `
  },
  'commercial-productivity': {
    id: 'commercial-productivity',
    title: "Commercial Spaces That Inspire Productivity",
    summary: "Behind the scenes of our STRATJURIS LAW PARTNERS project and how thoughtful design can boost workplace performance and employee satisfaction.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=1200&q=80",
    category: "Commercial Design",
    author: "Emma Rodriguez",
    date: "2025-01-05",
    readTime: "6 min read",
    tags: ["commercial design", "productivity", "workplace", "law firm"],
    content: `
      <p>When STRATJURIS LAW PARTNERS approached us to redesign their offices, they had one clear goal: create a workspace that would enhance productivity while reflecting their firm's values of professionalism and innovation. The result is a space that perfectly balances functionality with aesthetic appeal.</p>
      
      <h2>Understanding the Law Firm Environment</h2>
      
      <p>Legal work requires intense concentration, confidential conversations, and collaborative brainstorming sessions. Our design needed to accommodate all these activities while creating an environment that attracts top talent and impresses clients.</p>
      
      <p>We started with extensive interviews with partners, associates, and support staff to understand their daily workflows and pain points with the existing space. This research phase proved invaluable in shaping our design approach.</p>
      
      <h2>Zoning for Different Work Styles</h2>
      
      <p>The modern office needs to support various work styles, and law firms are no exception. We created distinct zones within the space:</p>
      
      <ul>
        <li><strong>Focus Zones:</strong> Private offices with excellent acoustics for concentration-intensive work</li>
        <li><strong>Collaboration Areas:</strong> Open meeting spaces with writable surfaces and flexible furniture</li>
        <li><strong>Informal Meeting Spots:</strong> Comfortable seating areas for quick consultations</li>
        <li><strong>Wellness Spaces:</strong> A quiet break room and meditation area for mental restoration</li>
      </ul>
      
      <h2>The Power of Natural Light</h2>
      
      <p>One of the most impactful changes we made was maximizing natural light throughout the office. We replaced solid walls with glass partitions that maintain privacy while allowing light to flow freely. The result is a brighter, more energizing workspace that reduces reliance on artificial lighting.</p>
      
      <blockquote>
        "The new office has transformed how we work. The natural light and thoughtful layout have noticeably improved our team's energy and focus." — Managing Partner, STRATJURIS
      </blockquote>
      
      <h2>Technology Integration</h2>
      
      <p>In today's legal practice, seamless technology integration is essential. We designed built-in solutions for cable management, incorporated wireless charging stations throughout the space, and created flexible presentation areas that can quickly adapt to different meeting sizes and formats.</p>
      
      <h2>Materials That Convey Authority</h2>
      
      <p>The material palette was carefully chosen to convey the firm's authority and reliability while maintaining a contemporary feel. Rich walnut wood paneling, sophisticated stone accents, and high-quality textiles create an environment that instills confidence in clients while providing a pleasant workspace for employees.</p>
      
      <h2>Measuring Success</h2>
      
      <p>Six months after the renovation, STRATJURIS reported a 25% increase in employee satisfaction scores and a noticeable improvement in client feedback about their office environment. The space has also become a powerful recruiting tool, helping them attract top legal talent in a competitive market.</p>
      
      <p>This project demonstrates that thoughtful commercial design isn't just about aesthetics—it's a strategic investment in business success.</p>
    `
  },
  'color-psychology': {
    id: 'color-psychology',
    title: "Color Psychology in Residential Interiors",
    summary: "Understanding how different colors affect mood and behavior, and how we use this knowledge to create homes that truly feel like sanctuaries.",
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80",
    category: "Design Tips",
    author: "Lisa Thompson",
    date: "2024-12-28",
    readTime: "4 min read",
    tags: ["color psychology", "interior design", "mood", "home decor"],
    content: `
      <p>Color is one of the most powerful tools in interior design, capable of transforming not just how a space looks, but how it feels. Understanding color psychology allows us to create homes that support their inhabitants' well-being and enhance their daily experiences.</p>
      
      <h2>The Science Behind Color</h2>
      
      <p>Research has consistently shown that colors can influence our emotions, energy levels, and even our behavior. While personal associations and cultural background play a role, there are some universal responses to color that we can leverage in our designs.</p>
      
      <h2>Warm Colors: Energy and Intimacy</h2>
      
      <p>Warm colors—reds, oranges, and yellows—tend to energize and create feelings of warmth and intimacy. These colors work beautifully in social spaces where you want to encourage conversation and connection:</p>
      
      <ul>
        <li><strong>Red:</strong> Stimulating and passionate, perfect for dining rooms where you want to create energy</li>
        <li><strong>Orange:</strong> Cheerful and welcoming, excellent for entryways and family rooms</li>
        <li><strong>Yellow:</strong> Uplifting and optimistic, wonderful for kitchens and breakfast nooks</li>
      </ul>
      
      <h2>Cool Colors: Calm and Focus</h2>
      
      <p>Cool colors—blues, greens, and purples—have a calming effect and can help create a sense of tranquility. These are ideal for spaces where relaxation and focus are priorities:</p>
      
      <ul>
        <li><strong>Blue:</strong> Calming and trustworthy, perfect for bedrooms and home offices</li>
        <li><strong>Green:</strong> Refreshing and balanced, excellent for any room where you spend extended time</li>
        <li><strong>Purple:</strong> Luxurious and creative, beautiful in bedrooms and creative spaces</li>
      </ul>
      
      <blockquote>
        "Color is a power which directly influences the soul." — Wassily Kandinsky
      </blockquote>
      
      <h2>Neutrals: The Perfect Foundation</h2>
      
      <p>Neutral colors provide a calming foundation that allows other elements in the room to shine. They're also incredibly versatile, adapting to different lighting conditions and seasonal decor changes. Our favorite neutrals include warm whites, soft grays, and earthy beiges.</p>
      
      <h2>Creating Color Harmony</h2>
      
      <p>The key to successful color schemes is balance. We often use the 60-30-10 rule: 60% of the room in a dominant neutral color, 30% in a secondary color, and 10% in a bold accent color. This creates visual interest while maintaining harmony.</p>
      
      <h2>Personal Considerations</h2>
      
      <p>While color psychology provides valuable guidelines, personal preference always takes precedence. We work closely with clients to understand their color preferences and associations, ensuring that their home reflects their personality while supporting their lifestyle goals.</p>
      
      <p>The result is a space that not only looks beautiful but feels emotionally supportive—a true sanctuary from the world outside.</p>
    `
  },
  'smart-home-future': {
    id: 'smart-home-future',
    title: "The Future of Smart Home Integration",
    summary: "How we're seamlessly incorporating smart technology into our designs without compromising aesthetic appeal or user experience.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
    category: "Technology",
    author: "Michael Park",
    date: "2024-12-20",
    readTime: "5 min read",
    tags: ["smart home", "technology", "automation", "future living"],
    content: `
      <p>The smart home revolution is here, but the challenge for designers is integrating advanced technology seamlessly into beautiful, livable spaces. Our approach focuses on making technology invisible while maximizing its benefits for daily life.</p>
      
      <h2>Invisible Integration</h2>
      
      <p>The best smart home technology is the kind you don't notice. We believe in creating "invisible" installations where the technology enhances the living experience without becoming the dominant visual element. This means hiding wiring, integrating controls into natural surfaces, and choosing devices that complement rather than compete with the design aesthetic.</p>
      
      <h2>Lighting That Learns</h2>
      
      <p>Smart lighting systems have come a long way from simple dimmer switches. Today's systems can:</p>
      
      <ul>
        <li>Automatically adjust color temperature throughout the day to support natural circadian rhythms</li>
        <li>Create custom scenes for different activities—reading, entertaining, relaxing</li>
        <li>Integrate with security systems and vacation modes</li>
        <li>Respond to natural light levels to maintain consistent illumination</li>
      </ul>
      
      <p>In our recent HILLSIDE residence project, we installed a comprehensive lighting system that the homeowners control entirely through intuitive wall panels that look like traditional switches but offer sophisticated programming capabilities.</p>
      
      <h2>Climate Comfort and Efficiency</h2>
      
      <p>Smart thermostats and zoned HVAC systems allow for unprecedented control over home comfort while significantly reducing energy consumption. These systems learn occupancy patterns and adjust accordingly, ensuring rooms are comfortable when needed and energy-efficient when empty.</p>
      
      <blockquote>
        "Technology should amplify human capability, not complicate it."
      </blockquote>
      
      <h2>Security Without Surveillance</h2>
      
      <p>Modern security systems offer peace of mind without making homes feel like fortresses. Smart locks, discrete cameras, and integrated alarm systems can be controlled remotely while maintaining the welcoming feel of a family home.</p>
      
      <h2>The Role of Voice Control</h2>
      
      <p>Voice assistants have become more sophisticated and can now control multiple systems through natural language commands. However, we always ensure that voice control supplements rather than replaces physical controls, maintaining accessibility for all users.</p>
      
      <h2>Future-Proofing Considerations</h2>
      
      <p>Technology evolves rapidly, so we design infrastructure that can adapt to future innovations. This includes robust networking capabilities, flexible conduit systems, and modular control panels that can be updated as technology advances.</p>
      
      <h2>The Human Element</h2>
      
      <p>Despite all the technological possibilities, we never lose sight of the human element. The goal is always to create homes that serve their inhabitants better, not to showcase technology for its own sake. The most successful smart home integrations are those that become intuitive extensions of how people naturally want to live.</p>
    `
  },
  'lighting-design': {
    id: 'lighting-design',
    title: "Lighting Design: Sculpting Spaces with Light",
    summary: "The art and science of lighting design, and how the right illumination can completely transform the feel and function of any space.",
    image: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?auto=format&fit=crop&w=1200&q=80",
    category: "Design Tips",
    author: "Anna Kowalski",
    date: "2024-12-15",
    readTime: "6 min read",
    tags: ["lighting design", "interior design", "ambiance", "functionality"],
    content: `
      <p>Lighting is often called the most important element in interior design, yet it's frequently an afterthought. Good lighting design can make a small space feel larger, create ambiance for any occasion, and even improve productivity and well-being. It's truly the art of sculpting with light.</p>
      
      <h2>The Three Layers of Lighting</h2>
      
      <p>Effective lighting design relies on three distinct layers, each serving a specific purpose:</p>
      
      <h3>Ambient Lighting</h3>
      <p>This is your general, overall illumination—the foundation layer that ensures safe navigation throughout the space. Ambient lighting can come from ceiling fixtures, recessed lights, or even large-scale table lamps.</p>
      
      <h3>Task Lighting</h3>
      <p>Focused lighting for specific activities like reading, cooking, or working. Task lighting should be bright enough to prevent eye strain while being positioned to minimize shadows on the work surface.</p>
      
      <h3>Accent Lighting</h3>
      <p>The decorative layer that adds visual interest and drama. This includes artwork lighting, architectural highlighting, and decorative fixtures that serve as focal points themselves.</p>
      
      <h2>Natural Light: The Starting Point</h2>
      
      <p>Before planning any artificial lighting, we analyze how natural light moves through a space throughout the day. Understanding these patterns allows us to complement and extend daylight hours while ensuring a smooth transition to evening illumination.</p>
      
      <p>Window treatments play a crucial role here—we often specify adjustable options that allow homeowners to modulate natural light for different times of day and seasons.</p>
      
      <blockquote>
        "Light is the most important person in the picture." — Claude Monet
      </blockquote>
      
      <h2>Color Temperature and Mood</h2>
      
      <p>The color temperature of light dramatically affects the mood of a space. Warm light (2700K-3000K) creates a cozy, intimate atmosphere perfect for living rooms and bedrooms. Cool light (4000K-5000K) promotes alertness and is ideal for kitchens and home offices.</p>
      
      <p>Many of our recent projects incorporate tunable LED systems that can adjust color temperature throughout the day, supporting natural circadian rhythms and adapting to different activities.</p>
      
      <h2>Common Lighting Mistakes</h2>
      
      <p>Over the years, we've identified several common lighting mistakes:</p>
      
      <ul>
        <li><strong>Over-relying on overhead lighting:</strong> This creates harsh shadows and unflattering illumination</li>
        <li><strong>Insufficient task lighting:</strong> Leading to eye strain and poor functionality</li>
        <li><strong>Ignoring dimming controls:</strong> Fixed brightness levels can't adapt to different needs</li>
        <li><strong>Poor fixture placement:</strong> Creating shadows where light is needed most</li>
      </ul>
      
      <h2>Fixture Selection</h2>
      
      <p>The fixtures themselves become important design elements. We consider scale, style, and proportion carefully, ensuring that lighting fixtures complement the overall design aesthetic while providing excellent illumination.</p>
      
      <p>Statement fixtures can serve as artwork, while more subtle fixtures disappear into the architecture, letting the light itself take center stage.</p>
      
      <h2>Creating Atmosphere</h2>
      
      <p>Perhaps most importantly, lighting design is about creating atmosphere. The right lighting can make a dinner party feel intimate, a workspace feel energizing, or a bedroom feel restful. It's this emotional impact that elevates lighting from mere functionality to true design artistry.</p>
      
      <p>Through thoughtful planning and skilled execution, lighting becomes a powerful tool for enhancing both the beauty and livability of any space.</p>
    `
  }
  ,
  'biophilic-design': {
    id: 'biophilic-design',
    title: "Biophilic Design: Bringing Nature Indoors",
    summary: "How nature-inspired materials, light, and greenery improve well-being and comfort in modern interiors.",
    image: "https://images.unsplash.com/photo-1501004318641-b39e6451bec6?auto=format&fit=crop&w=1200&q=80",
    category: "Design Philosophy",
    author: "Nina Patel",
    date: "2025-01-20",
    readTime: "5 min read",
    tags: ["biophilic", "nature", "well-being", "materials"],
    content: `
      <p>Biophilic design strengthens the human connection to nature through natural materials, patterns, and light. The result is calmer, healthier spaces that feel grounded and restorative.</p>
      <h2>Key Elements</h2>
      <ul>
        <li><strong>Natural Light:</strong> Maximize daylight and views</li>
        <li><strong>Greenery:</strong> Integrate plants and living walls</li>
        <li><strong>Materials:</strong> Wood, stone, clay, and natural fibers</li>
        <li><strong>Patterns:</strong> Organic forms and textures inspired by nature</li>
      </ul>
      <p>In our studio, we focus on subtle, sustainable interventions that bring warmth and vitality without overwhelming the space.</p>
    `
  }
};

// Get related blogs based on category
const getRelatedBlogs = (currentBlogId, category) => {
  return Object.values(dummyBlogDetails)
    .filter(blog => blog.id !== currentBlogId && blog.category === category)
    .slice(0, 3);
};

// Related blog card component
function RelatedBlogCard({ blog, onClick }) {
  const [cardRef, cardVisible] = useScrollAnimation(0.2);
  
  return (
    <div className="btw-related-blog-card" ref={cardRef} onClick={onClick}>
      <div className={`btw-related-blog-image-wrapper fade-in-scale ${cardVisible ? 'visible' : ''}`}>
        <img src={blog.image} alt={blog.title} className="btw-related-blog-image" />
      </div>
      <div className={`btw-related-blog-content fade-in-up ${cardVisible ? 'visible' : ''}`}>
        <div className="btw-related-blog-category">[{blog.category.toUpperCase()}]</div>
        <h4 className="btw-related-blog-title">{blog.title}</h4>
        <div className="btw-related-blog-meta">
          <span>{formatDate(blog.date)}</span>
          <span className="btw-blog-dot">•</span>
          <span>{blog.readTime}</span>
        </div>
      </div>
    </div>
  );
}

export default function BlogDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroRef, heroVisible] = useScrollAnimation(0.3);
  const [contentRef, contentVisible] = useScrollAnimation(0.2);
  const [relatedRef, relatedVisible] = useScrollAnimation(0.2);

  // Maintain a consistent ordered list of blog ids from dummy data
  const orderedIds = useMemo(() => Object.keys(dummyBlogDetails), []);
  const [prevNext, setPrevNext] = useState({ prev: null, next: null });

  useEffect(() => {
    const loadBlogDetails = () => {
      try {
        setLoading(true);
        setError(null);
        
        // Simulate loading delay for visual feedback
        setTimeout(() => {
          const blogData = dummyBlogDetails[id];
          if (!blogData) {
            setError('Blog post not found');
            setLoading(false);
            return;
          }
          
          setBlog(blogData);
          
          // Get related blogs
          if (blogData.category) {
            const related = getRelatedBlogs(id, blogData.category);
            setRelatedBlogs(related);
          }

          // Compute prev/next based on ordered ids
          const idx = orderedIds.indexOf(id);
          const prevId = idx > 0 ? orderedIds[idx - 1] : null;
          const nextId = idx >= 0 && idx < orderedIds.length - 1 ? orderedIds[idx + 1] : null;
          setPrevNext({ prev: prevId, next: nextId });
          
          setLoading(false);
        }, 500);
      } catch (err) {
        console.error('Error loading blog:', err);
        setError('Failed to load blog post');
        setLoading(false);
      }
    };

    loadBlogDetails();
  }, [id, orderedIds]);

  if (loading) {
    return (
      <main style={{background: 'transparent', minHeight: '100vh', color: '#181618', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <Loader 
          variant="dots" 
          category="general" 
          message={{ text: 'Loading Article', subtitle: 'Preparing design insights' }}
        />
      </main>
    );
  }

  if (error || !blog) {
    return (
      <main style={{background: 'transparent', minHeight: '100vh', color: '#181618', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        <div className="btw-blog-error">
          <h2>Article Not Found</h2>
          <p>{error || 'The article you\'re looking for doesn\'t exist.'}</p>
          <Link to="/blog">
            <button className="btw-cta">Back to Blog</button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main style={{background: 'transparent', minHeight: '100vh', color: '#181618'}}>
      {/* Hero Section */}
      <section className="btw-blog-detail-hero" ref={heroRef}>
        <div className="btw-blog-detail-hero-content">
          <div className={`btw-blog-detail-category subtitle-animate ${heroVisible ? 'visible' : ''}`}>
            [{blog.category.toUpperCase()}]
          </div>
          <h1 className={`btw-blog-detail-title hero-title-animate ${heroVisible ? 'visible' : ''}`}>
            {blog.title}
          </h1>
          <div className={`btw-blog-detail-meta description-animate ${heroVisible ? 'visible' : ''}`}>
            <span className="btw-blog-detail-author">By {blog.author}</span>
            <span className="btw-blog-dot">•</span>
            <span className="btw-blog-detail-date">{formatDate(blog.date)}</span>
            <span className="btw-blog-dot">•</span>
            <span className="btw-blog-detail-read-time">{blog.readTime}</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="btw-blog-detail-image-section">
        <div className="btw-blog-detail-image-wrapper">
          <img src={blog.image} alt={blog.title} className="btw-blog-detail-image" />
        </div>
      </section>

      {/* Content */}
      <section className="btw-blog-detail-content-section" ref={contentRef}>
        <div className={`btw-blog-detail-content fade-in-up ${contentVisible ? 'visible' : ''}`}>
          <div className="btw-blog-detail-summary">
            {blog.summary}
          </div>
          <div 
            className="btw-blog-detail-text"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </div>
      </section>

      {/* Navigation */}
      <section className="btw-blog-detail-navigation">
        <div className="btw-blog-detail-nav-content">
          <div style={{ display: 'flex', gap: 12 }}>
            <Link to="/blog" className="btw-blog-nav-link">
              <button className="btw-cta btw-cta-secondary"><span>← Back to Blog</span></button>
            </Link>
            <Link to={`/blog?category=${encodeURIComponent(blog.category)}`} className="btw-blog-nav-link">
              <button className="btw-cta btw-cta-secondary"><span>More {blog.category} →</span></button>
            </Link>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {prevNext.prev ? (
              <Link to={`/blog/${prevNext.prev}`} className="btw-blog-nav-link">
                <button className="btw-cta btw-cta-secondary"><span>← Previous</span></button>
              </Link>
            ) : <span />}
            {prevNext.next ? (
              <Link to={`/blog/${prevNext.next}`} className="btw-blog-nav-link">
                <button className="btw-cta btw-cta-secondary"><span>Next →</span></button>
              </Link>
            ) : <span />}
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedBlogs.length > 0 && (
        <section className="btw-blog-related-section" ref={relatedRef}>
          <div className={`btw-blog-related-header subtitle-animate ${relatedVisible ? 'visible' : ''}`}>
            [RELATED ARTICLES]
          </div>
          <h2 className={`btw-blog-related-title hero-title-animate ${relatedVisible ? 'visible' : ''}`}>
            MORE FROM OUR STUDIO
          </h2>
          <div className="btw-blog-related-grid">
            {relatedBlogs.map((relatedBlog) => (
              <RelatedBlogCard
                key={relatedBlog.id}
                blog={relatedBlog}
                onClick={() => navigate(`/blog/${relatedBlog.id}`)}
              />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}